import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

const FEATURE_NAMES: Record<string, Record<string, string>> = {
  glucophoto:      { en: 'GlucoPhoto',          es: 'GlucoPhoto',           de: 'GlucoPhoto' },
  'weekly-picks':  { en: 'Weekly Picks',         es: 'Picks de la semana',   de: 'Wöchentliche Picks' },
  'weekly-plan':   { en: 'Meal Plan',            es: 'Plan de comidas',      de: 'Mahlzeitenplan' },
  glucofy:         { en: 'Glucofy AI',           es: 'Glucofy IA',           de: 'Glucofy KI' },
};
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { registerFeatureRatingCallback, markFeatureRated, markFeatureSkipped, useFeatureRating } from '@/hooks/useFeatureRating';
import { Area, Botao, Chips } from '@/components/sistema';
import type { Chip } from '@/components/sistema';

/** As áreas do app que têm avaliação: o prefixo da rota → o nome do recurso. */
const AREAS_AVALIADAS: { prefixo: string; recurso: string }[] = [
  { prefixo: '/glucophoto', recurso: 'glucophoto' },
];

// ─── Manager (rendered once in App.tsx) ───────────────────────────────────────
/**
 * Mostra a folha de avaliação quando a pessoa SAI de uma área — não enquanto
 * ela ainda está lá dentro. Antes o gatilho era a desmontagem da tela
 * inicial do GlucoPhoto, e ir do GlucoPhoto para o histórico do GlucoPhoto
 * já disparava a pergunta, no meio do uso.
 */
export function FeatureRatingManager() {
  const [activeFeature, setActiveFeature] = useState<string | null>(null);
  const { pathname } = useLocation();
  const anterior = useRef(pathname);
  const { triggerRating } = useFeatureRating('glucophoto');

  useEffect(() => {
    return registerFeatureRatingCallback(setActiveFeature);
  }, []);

  useEffect(() => {
    const de = anterior.current;
    anterior.current = pathname;
    for (const area of AREAS_AVALIADAS) {
      const estava = de.startsWith(area.prefixo);
      const saiu = !pathname.startsWith(area.prefixo);
      if (estava && saiu && area.recurso === 'glucophoto') triggerRating();
    }
  }, [pathname, triggerRating]);

  if (!activeFeature) return null;

  return (
    <FeatureRatingSheet
      feature={activeFeature}
      onClose={() => setActiveFeature(null)}
    />
  );
}

// ─── Sheet ────────────────────────────────────────────────────────────────────
interface Props {
  feature: string;
  onClose: () => void;
}

type Nota = '1' | '2' | '3' | '4' | '5';
const NOTAS: Nota[] = ['1', '2', '3', '4', '5'];
type SimNao = 'yes' | 'no';

/**
 * Avaliação de um recurso, na folha que sobe de baixo.
 *
 * Compacta de propósito: a nota é uma fila de cinco botões redondos de 56px
 * (a palavra da nota escolhida aparece embaixo), as duas perguntas de sim/não
 * são uma linha cada (pergunta à esquerda, dois chips à direita), o
 * comentário é opcional. O X no canto e o "Agora não" ao lado do enviar
 * estão sempre à vista — a folha não passa de dois terços da tela.
 *
 * Lógica intocada: mesmo insert em `feature_ratings` com os mesmos campos,
 * mesmo `markFeatureRated`/`markFeatureSkipped`, mesmo fechamento em 300ms,
 * mesmo nome de recurso por idioma, mesmo "só envia com nota".
 */
function FeatureRatingSheet({ feature, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const lang = (i18n.language?.substring(0, 2) || 'es') as 'en' | 'es' | 'de';
  const featureDisplayName = FEATURE_NAMES[feature]?.[lang] ?? FEATURE_NAMES[feature]?.['en'] ?? feature;

  const [stars, setStars] = useState(0);
  const [wouldUseAgain, setWouldUseAgain] = useState<boolean | null>(null);
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [visible, setVisible] = useState(false);

  // Slide-up animation on mount
  useEffect(() => {
    const id = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const handleSkip = () => {
    markFeatureSkipped(feature);
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const handleSubmit = async () => {
    if (stars === 0 || !user) return;
    setSaving(true);
    try {
      await supabase.from('feature_ratings' as any).insert({
        user_id: user.id,
        feature,
        rating: stars,
        would_use_again: wouldUseAgain,
        would_recommend: wouldRecommend,
        expected_result: comment.trim() || null,
      });
      markFeatureRated(feature);
    } catch (err) {
      console.error('[FeatureRating] save failed:', err);
    } finally {
      setSaving(false);
      setVisible(false);
      setTimeout(onClose, 300);
    }
  };

  const OPCOES_SIM_NAO: Chip<SimNao>[] = [
    { valor: 'yes', rotulo: t('feature_rating.yes') },
    { valor: 'no', rotulo: t('feature_rating.no') },
  ];

  /** null continua sendo "não respondeu" — é o que vai para o banco. */
  const comoValor = (v: boolean | null) => (v === null ? null : v ? 'yes' : 'no') as SimNao | null;

  const PerguntaSimNao = ({ pergunta, valor, onEscolher }: { pergunta: string; valor: SimNao | null; onEscolher: (v: SimNao) => void }) => (
    <div className="flex items-center justify-between gap-3" style={{ padding: '14px var(--gutter) 0' }}>
      <p className="min-w-0 flex-1 font-semibold leading-snug" style={{ fontSize: 'var(--t-corpo)' }}>{pergunta}</p>
      <div className="flex shrink-0 gap-2">
        {OPCOES_SIM_NAO.map((o) => {
          const ativo = o.valor === valor;
          return (
            <button
              key={o.valor}
              type="button"
              onClick={() => onEscolher(o.valor)}
              className={`toque rounded-full px-5 font-semibold ${ativo ? '' : 'bg-secondary'}`}
              style={{
                minHeight: 'var(--alvo-min)',
                fontSize: 'var(--t-apoio)',
                backgroundColor: ativo ? 'hsl(var(--accent))' : undefined,
                color: ativo ? 'hsl(var(--accent-foreground))' : 'var(--tinta)',
              }}
            >
              {o.rotulo}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <div
        onClick={handleSkip}
        className="fixed inset-0 bg-black/50"
        style={{
          zIndex: 9998,
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />

      <div
        className="fixed bottom-0 left-0 right-0 overflow-y-auto bg-card"
        style={{
          zIndex: 9999,
          borderRadius: '24px 24px 0 0',
          maxHeight: '70vh',
          paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
          transform: visible ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 0.3s ease-out',
          boxShadow: '0 -12px 40px rgba(0,0,0,0.35)',
        }}
      >
        <div className="flex items-start justify-between gap-3" style={{ padding: '20px 12px 0 var(--gutter)' }}>
          <p className="font-bold leading-tight" style={{ fontSize: 'var(--t-item)', paddingTop: 8 }}>
            {t('feature_rating.title', { feature: featureDisplayName })}
          </p>
          <button
            type="button"
            onClick={handleSkip}
            aria-label={t('feature_rating.skip')}
            className="toque flex shrink-0 items-center justify-center rounded-full bg-secondary"
            style={{ width: 'var(--alvo-min)', height: 'var(--alvo-min)' }}
          >
            <X className="h-6 w-6" strokeWidth={2.5} />
          </button>
        </div>

        {/* A nota: cinco botões redondos, a palavra da escolhida embaixo. */}
        <div className="flex justify-between" style={{ padding: '18px var(--gutter) 0' }}>
          {NOTAS.map((n) => {
            const ativo = Number(n) === stars;
            return (
              <button
                key={n}
                type="button"
                onClick={() => setStars(Number(n))}
                aria-label={t(`rating.star_${n}`)}
                aria-pressed={ativo}
                className={`toque flex items-center justify-center rounded-full font-bold ${ativo ? '' : 'bg-secondary'}`}
                style={{
                  width: 'var(--alvo-min)',
                  height: 'var(--alvo-min)',
                  fontSize: 'var(--t-item)',
                  backgroundColor: ativo ? 'hsl(var(--accent))' : undefined,
                  color: ativo ? 'hsl(var(--accent-foreground))' : 'var(--tinta)',
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
        <p
          className="text-center font-semibold leading-snug"
          style={{ padding: '10px var(--gutter) 0', fontSize: 'var(--t-corpo)', color: 'var(--tinta-apoio)', minHeight: 'calc(var(--t-corpo) * 1.4 + 10px)' }}
        >
          {stars > 0 ? t(`rating.star_${stars}`) : t('rating.main_question')}
        </p>

        <PerguntaSimNao pergunta={t('feature_rating.use_again')} valor={comoValor(wouldUseAgain)} onEscolher={(v) => setWouldUseAgain(v === 'yes')} />
        <PerguntaSimNao pergunta={t('feature_rating.recommend')} valor={comoValor(wouldRecommend)} onEscolher={(v) => setWouldRecommend(v === 'yes')} />

        <div style={{ paddingTop: 16 }}>
          <Area
            valor={comment}
            onMudar={setComment}
            placeholder={t('feature_rating.comment_placeholder')}
            linhas={2}
            maximo={200}
          />
        </div>

        <div style={{ padding: '16px var(--gutter) 0', display: 'grid', gap: 10 }}>
          <Botao
            rotulo={saving ? t('common.saving') : t('feature_rating.submit')}
            desabilitado={stars === 0 || saving}
            onClick={handleSubmit}
          />
          <Botao
            rotulo={t('feature_rating.skip')}
            variante="secundaria"
            onClick={handleSkip}
          />
        </div>
      </div>
    </>
  );
}
